import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { cloneDeep } from 'lodash-es';
import { interval, Subscription } from 'rxjs';
import { PAYMENT_CHECK_INTERVAL, PaymentStatus } from '../../../../../domain/common.model';
import { ConfigurationStatus, ConfigurationType } from '../../../../../domain/consts';
import { PropertyType } from '../../../../../domain/dictionary.model';
import { SalespointDocument } from '../../../../../domain/document.model';
import { Policy } from '../../../../../domain/policy.model';
import {
    CessionPartyType,
    InsuredProductProperty,
    PropertyConfiguration,
    PropertyFlatElements,
    PropertyHouseElements,
    SummaryType
} from '../../../../../domain/product.domain';
import { IOfferService } from '../../../service/ioffer.service';
import { IOfferAction } from '../../../store/ioffer.action';
import { IOfferState } from '../../../store/ioffer.state';
import { OfferAction } from '../../../store/offer.action';
import { PolicyAction } from '../../../store/policy.action';

@Component({
    selector: 'app-property-summary-box',
    templateUrl: './property-summary-box.component.html',
    styleUrls: ['./property-summary-box.component.scss']
})
export class PropertySummaryBoxComponent implements OnInit, OnDestroy {

    private subscriptions: Subscription[] = [];

    @Input() document: SalespointDocument;
    @Input() policyNumber: string = null;
    @Input() readonly = false;
    @Input() type: SummaryType = SummaryType.CALCULATION;
    @Input() fullInsuranceDate = true;
    @Input() iOffer = false;
    @Input() previewEnabled: boolean = true;

    inProgress: string[] = [];
    paid: string[] = [];
    cessionPartyType = CessionPartyType;
    summaryType = SummaryType;


    get hasPayment() {
        return this.iOffer && this.type === SummaryType.POLICY;
    }

    get policy() {
        return this.document as Policy;
    }

    get elements() {
        const code = this.configuration.propertyType
            ? this.configuration.propertyType.code
            : PropertyType.HOUSE;
        return code === PropertyType.HOUSE
            ? PropertyHouseElements
            : PropertyFlatElements;
    }

    get insuranceScope() {
        const configuration = this.configuration;
        const result = this.elements
            .map(e => {
                return {label: e.label, available: configuration.elementList[e.type].selected}
            });
        result.push({label: 'OFFER_CONFIG.FLOOD_RISK', available: configuration.floodRisk.selected});
        result.push({label: 'OFFER_CONFIG.OC_PERSONAL', available: configuration.personalLiability.selected});
        return result
            .filter(element => element.available)
            .map(element => this.translateService.instant(element.label))
            .join(', ');
    }

    get product() {
        return this.document.productList
            .find(p => p.configuration.configurationType === ConfigurationType.PROPERTY
            ) as InsuredProductProperty;
    }

    get configuration(): PropertyConfiguration {
        return this.document.productList
            .map(p => p.configuration)
            .find(p => p.configurationType === ConfigurationType.PROPERTY) as PropertyConfiguration;
    }

    get hasFinishedConfiguration() {
        if (!this.configuration) {
            return false;
        }
        return this.configuration.configurationStatus === ConfigurationStatus.COMPLETED;
    }

    get printoutLabel() {
        switch (this.type) {
            case SummaryType.CALCULATION:
                return 'LABEL.CALCULATION_PRINTOUT';
            case SummaryType.OFFER:
                return 'LABEL.OFFER_PRINTOUT';
            case SummaryType.POLICY:
                return 'LABEL.POLICY_PRINTOUT';
        }
        return null;
    }

    constructor(private store: Store,
                private iofferService: IOfferService,
                private translateService: TranslateService) {
    }


    ngOnInit(): void {
        this.subscriptions.push(
            this.store.select(IOfferState.getInProgress).subscribe(inProgress => this.inProgress = cloneDeep(inProgress)),
            interval(PAYMENT_CHECK_INTERVAL).subscribe(() => {
                this.inProgress
                    .filter(order => this.paid.indexOf(order) === -1)
                    .forEach(token => this.iofferService.checkPaymentStatus(token)
                        .subscribe(payment => {
                            if (payment.status === PaymentStatus.SUCCESS) {
                                this.paid.push(token);
                            }
                        })
                    )
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }


    editCession() {
        const product = this.document.productList
            .find(p => p.configuration.configurationType === ConfigurationType.PROPERTY);
        if (product) {
            this.store.dispatch(new OfferAction.OpenCessionAside(product));
        }
    }

    deleteCession() {
        const product = this.document.productList
            .find(p => p.configuration.configurationType === ConfigurationType.PROPERTY);
        this.store.dispatch(new OfferAction.DeleteCession(this.document.id, product.id));
    }


    preview() {
        this.store.dispatch(new OfferAction.ReadonlyProperty(this.product));
    }

    download(all) {
        switch (this.type) {
            case SummaryType.CALCULATION:
                this.store.dispatch(new OfferAction.PrintoutCalculation(this.document.id, this.product.id, all));
                break;
            case SummaryType.OFFER:
                this.store.dispatch(
                    this.iOffer
                        ? new IOfferAction.PrintoutOffer(this.document.id, this.product.id, all)
                        : new OfferAction.PrintoutOffer(this.document.id, this.product.id, all)
                );
                break;
            case SummaryType.POLICY:
                this.store.dispatch(
                    this.iOffer
                        ? new IOfferAction.PrintoutPolicy(this.document.id, all)
                        : new PolicyAction.Printout(this.document.id, all)
                );
                break;
        }
    }

    downloadSummary() {
        this.store.dispatch(
            this.iOffer
                ? new IOfferAction.PrintoutSummary(this.document.id)
                : new PolicyAction.PrintoutSummary(this.document.id)
        );
    }

    pay(policy: Policy) {
        this.store.dispatch(new IOfferAction.PayPolicy(policy.orderId));
    }

    toPay(orderId: string) {
        return this.inProgress.indexOf(orderId) === -1 && this.paid.indexOf(orderId) === -1;
    }

    isPending(orderId: string) {
        return this.inProgress.indexOf(orderId) !== -1 && this.paid.indexOf(orderId) === -1;
    }

    isPaid(orderId: string) {
        return this.paid.indexOf(orderId) !== -1;
    }
}
